import dayjs from "dayjs";

const isPatientBookingRecord = (row) => {
  if (!row || typeof row !== "object" || Array.isArray(row)) return false;
  const notes = `${row.notes || ""} ${row.bookingNotes || ""}`;
  if (/patient portal/i.test(notes)) return true;
  return Boolean(
    row.preferredDate ||
      row.preferred_date ||
      row.preferredTime ||
      row.preferred_time ||
      row.bookingDate ||
      row.booking_date
  );
};

const flattenBookings = (bookings) => {
  const rows = [];
  (Array.isArray(bookings) ? bookings : []).forEach((booking) => {
    const nested =
      booking?.bookedTests || booking?.labTests || booking?.tests || booking?.patientLabTests;
    const parentIsBooking = isPatientBookingRecord(booking);
    if (Array.isArray(nested) && nested.length > 0 && (parentIsBooking || nested.some(isPatientBookingRecord))) {
      nested.forEach((test) => {
        if (!parentIsBooking && !isPatientBookingRecord(test)) return;
        rows.push({
          ...booking,
          ...test,
          prescriptionId:
            test?.prescriptionId ??
            test?.prescription_id ??
            booking?.prescriptionId ??
            booking?.prescription_id,
          patientId: test?.patientId ?? test?.patient_id ?? booking?.patientId ?? booking?.patient_id,
          labTest:
            test?.labTest || test?.lab_test || booking?.labTest || booking?.lab_test || null,
          notes: test?.notes || booking?.notes,
          preferredDate: test?.preferredDate ?? booking?.preferredDate ?? booking?.preferred_date,
          preferredTime: test?.preferredTime ?? booking?.preferredTime ?? booking?.preferred_time,
        });
      });
      return;
    }
    if (Array.isArray(booking?.patientLabTests) || Array.isArray(booking?.medicines)) return;
    const patientId = booking?.patientId ?? booking?.patient_id;
    const labTestId = booking?.labTestId ?? booking?.lab_test_id ?? booking?.testId ?? booking?.test_id;
    if (patientId == null || patientId === "") return;
    if ((labTestId == null || labTestId === "") && !parentIsBooking) return;
    rows.push(booking);
  });
  return rows;
};

const parseBookingDate = (value) => {
  if (Array.isArray(value) && value.length >= 3) {
    const [year, month, day, hour = 0, minute = 0] = value;
    const parsed = dayjs(
      new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute))
    );
    return parsed.isValid() ? parsed : null;
  }
  if (value == null || value === "") return null;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const readTestIdentity = (row) => {
  const nested = row?.labTest || row?.lab_test || null;
  const labTestId =
    row?.labTestId ??
    row?.lab_test_id ??
    row?.testId ??
    row?.test_id ??
    nested?.labTestId ??
    nested?.lab_test_id ??
    nested?.testId ??
    nested?.test_id ??
    null;
  const testName =
    row?.testName ||
    row?.resolvedTestName ||
    row?.labTestName ||
    row?.lab_test_name ||
    row?.name ||
    nested?.testName ||
    nested?.labTestName ||
    nested?.name ||
    "";
  return { labTestId, testName: String(testName).trim() };
};

const sameId = (left, right) =>
  left != null &&
  left !== "" &&
  right != null &&
  right !== "" &&
  String(left) === String(right);

const sameTestName = (left, right) => {
  const a = String(left || "").trim().toLowerCase();
  const b = String(right || "").trim().toLowerCase();
  return Boolean(a) && a === b;
};

const readPreferredWhen = (booking) => {
  const rawDate =
    booking?.preferredDate ||
    booking?.preferred_date ||
    booking?.bookingDate ||
    booking?.booking_date ||
    null;
  let date = parseBookingDate(rawDate);
  if (!date) {
    const notes = String(booking?.notes || "");
    const match = notes.match(/on\s+(\d{4}-\d{2}-\d{2})/i);
    if (match) date = parseBookingDate(match[1]);
  }
  const directTime =
    booking?.preferredTime || booking?.preferred_time || booking?.appointmentTime || "";
  let time = String(directTime || "").trim();
  if (!time) {
    const notes = String(booking?.notes || "");
    const match = notes.match(/(?:Preferred time:| at )\s*(\d{1,2}:\d{2}\s*[AP]M)/i);
    time = match?.[1]?.trim() || "";
  }
  const whenLabel = [date ? date.format("DD MMM YYYY") : "", time].filter(Boolean).join(" • ");
  return { date, time, whenLabel };
};

const buildPrescriptionNameLookup = (prescriptions) => {
  const byKey = new Map();
  (Array.isArray(prescriptions) ? prescriptions : []).forEach((prescription) => {
    const prescriptionId =
      prescription?.id ?? prescription?.prescriptionId ?? prescription?.prescription_id;
    const tests = prescription?.patientLabTests || prescription?.labTests || [];
    (Array.isArray(tests) ? tests : []).forEach((test) => {
      const { labTestId, testName } = readTestIdentity(test);
      if (!testName || labTestId == null) return;
      if (prescriptionId == null || prescriptionId === "") return;
      byKey.set(`${prescriptionId}:${labTestId}`, testName);
    });
  });
  return byKey;
};

export const createPatientBookedTestMatcher = (bookings = [], prescriptions = []) => {
  const nameLookup = buildPrescriptionNameLookup(prescriptions);
  const entries = [];

  flattenBookings(bookings).forEach((booking) => {
    const { labTestId, testName: bookingName } = readTestIdentity(booking);
    const prescriptionId = booking?.prescriptionId ?? booking?.prescription_id;
    const patientId = booking?.patientId ?? booking?.patient_id;
    if (patientId == null || patientId === "") return;
    const lookedUpName =
      prescriptionId != null && prescriptionId !== "" && labTestId != null
        ? nameLookup.get(`${prescriptionId}:${labTestId}`)
        : "";
    const testName = bookingName || lookedUpName || "";
    if ((labTestId == null || labTestId === "") && !testName) return;
    const when = readPreferredWhen(booking);
    entries.push({
      booked: true,
      prescriptionId,
      patientId,
      labTestId,
      testName,
      whenLabel: when.whenLabel,
      preferredDate: when.date,
      preferredTime: when.time,
    });
  });

  return (row, parent) => {
    const { labTestId, testName } = readTestIdentity(row);
    const prescriptionId =
      row?.prescriptionId ??
      row?.prescription_id ??
      parent?.prescriptionId ??
      parent?.prescription_id ??
      parent?.id;
    const patientId = row?.patientId ?? row?.patient_id ?? parent?.patientId ?? parent?.patient_id;
    if (patientId == null || patientId === "") return null;

    return (
      entries.find((info) => {
        if (!sameId(info.patientId, patientId)) return false;
        const bookingPrescriptionId = info.prescriptionId;
        if (
          bookingPrescriptionId != null &&
          bookingPrescriptionId !== "" &&
          prescriptionId != null &&
          prescriptionId !== "" &&
          !sameId(bookingPrescriptionId, prescriptionId)
        ) {
          return false;
        }
        if (info.labTestId != null && info.labTestId !== "" && labTestId != null && labTestId !== "") {
          return sameId(info.labTestId, labTestId);
        }
        return sameTestName(info.testName, testName);
      }) || null
    );
  };
};

const findPrescription = (prescriptions, booking) => {
  const prescriptionId = booking?.prescriptionId ?? booking?.prescription_id;
  const patientId = booking?.patientId ?? booking?.patient_id;
  if (prescriptionId == null || prescriptionId === "") return null;
  const list = Array.isArray(prescriptions) ? prescriptions : [];
  return (
    list.find((prescription) => {
      const id = prescription?.id ?? prescription?.prescriptionId ?? prescription?.prescription_id;
      if (!sameId(id, prescriptionId)) return false;
      const prescriptionPatientId = prescription?.patientId ?? prescription?.patient_id;
      if (
        patientId != null &&
        patientId !== "" &&
        prescriptionPatientId != null &&
        prescriptionPatientId !== ""
      ) {
        return sameId(prescriptionPatientId, patientId);
      }
      return true;
    }) || null
  );
};

/** Rows for patient bookings that are not already present in the path-desk report list. */
export const buildMissingPatientBookedTestRows = (bookings, prescriptions, existingRows = []) => {
  const matcher = createPatientBookedTestMatcher(bookings, prescriptions);
  const existing = Array.isArray(existingRows) ? existingRows : [];

  return flattenBookings(bookings)
    .map((booking) => {
      const prescription = findPrescription(prescriptions, booking);
      const { labTestId, testName: bookingName } = readTestIdentity(booking);
      const lookedUp = matcher(
        {
          ...booking,
          labTestId,
          testName: bookingName,
        },
        prescription
      );
      const bookingPatientId = booking?.patientId ?? booking?.patient_id ?? null;
      if (bookingPatientId == null || bookingPatientId === "") return null;
      const testName = lookedUp?.testName || bookingName || (labTestId != null ? `Lab Test #${labTestId}` : "Lab Test");
      const candidate = {
        ...booking,
        labTestId,
        testId: labTestId,
        testName,
        prescriptionId: booking?.prescriptionId ?? booking?.prescription_id ?? null,
        patientId: bookingPatientId,
        patientName:
          booking?.patientName ||
          booking?.patient_name ||
          (prescription && sameId(prescription?.patientId ?? prescription?.patient_id, bookingPatientId)
            ? prescription?.patientName || prescription?.patient_name
            : "") ||
          "—",
      };
      const bookingInfo = matcher(candidate, prescription);
      if (!bookingInfo) return null;
      const alreadyShown = existing.some((row) => {
        const rowPatientId = row?.patientId ?? row?.patient_id;
        if (!sameId(rowPatientId, candidate.patientId)) return false;
        const rowPrescriptionId = row?.prescriptionId ?? row?.prescription_id;
        if (
          candidate.prescriptionId != null &&
          candidate.prescriptionId !== "" &&
          rowPrescriptionId != null &&
          rowPrescriptionId !== "" &&
          !sameId(rowPrescriptionId, candidate.prescriptionId)
        ) {
          return false;
        }
        const rowIdentity = readTestIdentity(row);
        if (labTestId != null && labTestId !== "" && rowIdentity.labTestId != null && rowIdentity.labTestId !== "") {
          return sameId(rowIdentity.labTestId, labTestId);
        }
        return sameTestName(rowIdentity.testName, testName);
      });
      if (alreadyShown) return null;

      const when = readPreferredWhen(booking);
      return {
        ...candidate,
        code: `patient-booking-${candidate.prescriptionId ?? "rx"}-${labTestId ?? testName}`,
        reportId: "—",
        result: "—",
        normalRange: "—",
        resultStatus: "PENDING",
        reportDate: when.date ? when.date.format("YYYY-MM-DD") : null,
        createdAt: when.date ? when.date.format("YYYY-MM-DD") : prescription?.createdAt || null,
        preferredTime: when.time,
        whenLabel: when.whenLabel,
        doctorName: prescription?.doctorName || candidate.doctorName || "—",
        doctorId: prescription?.doctorId ?? candidate.doctorId ?? null,
        bookedByPatient: true,
        notes: booking?.notes || "Patient portal test booking",
      };
    })
    .filter(Boolean);
};
